import type { TError, TLessonProgressBase } from '@onesource/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';
import { invalidateMyAnalytics } from '../my-analytics/get-my-analytics';
import { getMySpacesQueryKey } from '../spaces/get-my-spaces';

/**
 * Mark a content block complete for the current user.
 *
 * The backend cascades completion upward (block → lesson → parent lesson →
 * module) and verifies action blocks (quiz/form/gcs upload/event) against their
 * real record. On success we invalidate ALL lesson-progress and module-progress
 * queries so every affected bar (lesson, parent card, module) refreshes, plus the
 * space/sub-space lists so a newly-unlocked module updates without a page refresh.
 *
 * The mutation variable is the content block id.
 */
export const useCompleteBlock = () => {
  const queryClient = useQueryClient();

  return useMutation<TLessonProgressBase, TError, string>({
    mutationFn: async (blockId: string) => {
      const res = await axios.post<TLessonProgressBase>(`/community-hub/blocks/${blockId}/complete`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'progress'] });
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'module-progress'] });
      // Completing a video view increments its watch count — refresh the
      // "Watched Nx" indicators right away.
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'video-watch-counts'] });
      // Sub-space listing carries per-module isLocked/prerequisiteName — refresh it
      // so completing a prerequisite unlocks the next module immediately.
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'spaces'] });
      // The space catalog carries the same lock state on its `children`, and backs
      // the sidebar's sub-space rows. Its key is `my-spaces`, which the prefix
      // above does NOT match, so it needs its own invalidation or the menu keeps
      // showing a module as locked after its prerequisite is finished.
      queryClient.invalidateQueries({ queryKey: getMySpacesQueryKey() });
      // A completed block may satisfy a My Analytics requirement.
      invalidateMyAnalytics(queryClient);
    },
  });
};
